import React, { useState, useRef } from "react";
import { intl, DmsCheckBox } from "dmsbasereact";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { DmsButton, DmsTableButton, HTTP_STATUS, fetchData } from "dmsbasereact";
import EditIcon from "@mui/icons-material/Edit";
import Swal from "sweetalert2";
import { DmsUploadOnly } from "../DmsUploadOnly/DmsUploadOnly";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

export const DmsTableEditTrainingMaterialButton = ({
  button_icon = <EditIcon />,
  row,
  needs_authentication,
  title = intl.get("EditTrainingMaterial").d("Edit Training Material"),
  table,
  button_label,
  button_text,
  variant = "primary",
  disabled = false,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [isMandatory, setIsMandatory] = useState(row?.is_mandatory ?? true);
  const uploadRef = useRef(null);

  const handleButton = () => {
    setIsMandatory(row?.is_mandatory ?? true);
    setModalOpen(true);
  };

  const handleSave = async () => {
    const files = uploadRef.current?.getSelectedFiles();

    if (!files || files.length === 0) {
      Swal.fire({
        icon: "warning",
        title: intl.get("MissingInformation").d("Missing Information"),
        text: intl.get("PleaseSelectFile").d("Please select a file."),
      });
      return;
    }

    if (!row || !row.pk) {
      setErrorText(intl.get("MissingMaterialInfo").d("Missing training material information."));
      setErrorModalOpen(true);
      setModalOpen(false);
      return;
    }

    let auth = needs_authentication;
    if (!auth && table && table.current) {
      auth = table.current.needs_authentication;
    }

    try {
      const updatePayload = { is_mandatory: isMandatory };

      // Upload new file if selected
      if (files && files.length > 0) {
        const file = files[0];
        const uploadRes = await fetchData(
          `/training_material/file_upload`,
          "POST",
          auth ? true : null,
          file,
          true,
          true,
          null,
          null,
          "multipart/form-data"
        );

        if (!(uploadRes.status === HTTP_STATUS.CREATED_201 && uploadRes.data?.data?.pk)) {
          throw new Error(uploadRes.data?.detail || "Failed to upload document");
        }

        updatePayload.file_pk = uploadRes.data.data.pk;
      }

      // Update the training material
      const updateRes = await fetchData(
        `/training_material/${row.pk}`,
        "PUT",
        auth ? true : null,
        updatePayload,
        true,
        true
      );

      if (updateRes.status !== HTTP_STATUS.OK_200) {
        throw new Error(updateRes?.data?.detail || "Failed to update training material.");
      }

      Swal.fire({
        icon: "success",
        title: intl.get("Success").d("Success"),
        text: intl.get("TrainingMaterialUpdated").d("Training material updated successfully."),
      });

      if (table?.current?.updateTable) {
        table.current.updateTable();
      }

      setModalOpen(false);
    } catch (error) {
      setErrorText(error.message || intl.get("ErrorUpdatingMaterial").d("Error updating training material"));
      setErrorModalOpen(true);
      setModalOpen(false);
    }
  };

  const modalContent = (
    <>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box sx={modalStyle}>
          <Typography id="modal-title" variant="h6" component="h2">
            {title}
          </Typography>

          <Box sx={{ mt: 2 }}>
            <DmsCheckBox
              label={intl.get("Mandatory").d("Mandatory")}
              checked={isMandatory}
              onChange={(e) => setIsMandatory(e?.target?.checked ?? e)}
            />

            <Typography variant="body2" sx={{ mb: 1, mt: 2 }}>
              <strong>{intl.get("CurrentFile").d("Current File")}:</strong> {row?.file?.original_filename || "-"}
            </Typography>

            <Typography variant="body2" sx={{ mb: 2 }}>
              {intl.get("SelectNewFileToReplace").d("Select a new file to replace the current one:")}
            </Typography>

            <DmsUploadOnly ref={uploadRef} autoUpload={false} accept={{ 'video/mp4': ['.mp4'] }} />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
              {intl.get("VideoFilesSupported").d("Video files supported (.MP4)")}
            </Typography>
          </Box>

          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <DmsButton
              variant="secondary"
              text={intl.get("Cancel").d("Cancel")}
              onClick={() => setModalOpen(false)}
            />
            <DmsButton
              variant="primary"
              onClick={handleSave}
              text={intl.get("Save").d("Save")}
            />
          </Box>
        </Box>
      </Modal>

      <Modal
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        aria-labelledby="error-modal-title"
        aria-describedby="error-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography id="error-modal-title" variant="h6" component="h2">
            {intl.get("Error").d("Error")}
          </Typography>
          <Typography id="error-modal-description" sx={{ mt: 2 }}>
            {errorText}
          </Typography>
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <DmsButton
              variant="secondary"
              text={intl.get("Ok").d("Ok")}
              onClick={() => setErrorModalOpen(false)}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );

  return (
    <>
      <DmsTableButton
        table={table}
        variant={variant}
        text={button_text}
        label={button_label}
        icon={button_icon}
        disabled={disabled}
        onClick={handleButton}
        title={title}
      />
      {modalContent}
    </>
  );
};
